import { Injectable } from '@angular/core';
import type { FormGroup } from '@angular/forms';

import type {
  CreateSeatPayload,
  DeactivateSeatPayload,
  SeatPayload,
  UpdateSeatPayload,
  // eslint-disable-next-line boundaries/element-types
} from '@core/tellent-data'; // FIXME
import type { TellentApps, TellentMember } from '@core/tellent-types';
import { MEMBER_LISTED_ONLY, SeatKind } from '@core/tellent-types';

import type { SeatType } from '../../add-member-dialog/access-roles-table/access-roles-table.component';

export type EditMemberForm = {
  seats: FormGroup<{
    recruitee: FormGroup<SeatType>;
    kiwi: FormGroup<SeatType>;
  }>;
};

@Injectable({ providedIn: 'root' })
export class EditMemberPermissionsAccessService {
  public resolveSeatsPayload(
    member: TellentMember,
    formGroup: FormGroup<EditMemberForm>,
    apps: TellentApps[],
  ) {
    const form = formGroup.getRawValue();

    const formSeats = [
      { ...form.seats.recruitee, kind: SeatKind.Recruitment },
      { ...form.seats.kiwi, kind: SeatKind.CoreHR },
    ];

    return formSeats.map(seat => this.createPayload(seat, member, apps)).filter(Boolean);
  }

  private createPayload(
    seat: {
      kind: SeatKind;
      toggle: boolean | null;
      roleExternalId: string | null;
      appGuid: string | null;
    },
    member: TellentMember,
    apps: TellentApps[],
  ): SeatPayload {
    const app = apps.find(app => app.kind === seat.kind);

    // A pending app's seat form is disabled and its state must not be
    // touched while the backend is still setting the app up; without this
    // guard an untouched pending seat (toggle initialized to false) would
    // emit a deactivate payload on every save.
    if (app?.status === 'pending') return <SeatPayload>(<unknown>undefined);

    const toggleEnabled = !!seat.toggle;
    const roleExternalId = toggleEnabled
      ? this.resolveRoleExternalId(seat.roleExternalId!)
      : undefined;
    const access = seat.roleExternalId !== MEMBER_LISTED_ONLY;
    const appGuid = app?.guid!;
    const memberGuid = member.guid;
    const memberSeatGuid = member.seats.find(
      memberSeat => memberSeat.app.kind === seat.kind,
    )?.guid!;

    if (!seat.appGuid) {
      return <SeatPayload>(
        (toggleEnabled
          ? this.getCreatePayload(access, appGuid, memberGuid, roleExternalId!)
          : undefined)
      );
    }
    if (!toggleEnabled) {
      return this.getDeactivatePayload(memberSeatGuid);
    }

    return this.getUpdatePayload(access, toggleEnabled, memberSeatGuid, roleExternalId!);
  }

  private getCreatePayload(
    access: boolean,
    appGuid: string,
    memberGuid: string,
    roleExternalId: string,
  ): CreateSeatPayload {
    return {
      access,
      active: true,
      appGuid,
      memberGuid,
      roleExternalId,
    };
  }

  private getDeactivatePayload(seatGuid: string): DeactivateSeatPayload {
    return {
      active: false,
      guid: seatGuid,
    };
  }

  private getUpdatePayload(
    access: boolean,
    active: boolean,
    seatGuid: string,
    roleExternalId: string,
  ): UpdateSeatPayload {
    return { access, active, guid: seatGuid, roleExternalId };
  }

  private resolveRoleExternalId(role: string): string {
    return role === MEMBER_LISTED_ONLY ? '' : role;
  }
}
